import { Colors, Icon, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import _ from 'lodash'
import React from 'react'
import ReactDOM from 'react-dom'
import Dropzone from 'react-dropzone'

import apis from 'browser/app/models/apis'
import { Entity } from 'shared-libs/models/entity'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { MasterDetail } from 'browser/components/atomic-elements/atoms/master-detail/master-detail'
import { Column, Table } from 'browser/components/atomic-elements/atoms/table'
import {
  TransientEntityDetailCard,
} from 'browser/components/atomic-elements/organisms/entity/transient-entity-detail-card/transient-entity-detail-card'

import 'browser/components/atomic-elements/organisms/internal-tools/ingestion-test-modal/_test-payload-config.scss'

const PAYLOAD_CONFIG_PATH = 'core_integrations_ingestion_multiEntity_payloadConfig'

interface ITestPayloadConfigProps {
  entity: any
}

interface ITestPayloadConfigState {
  sample: string
  fileName: string
  isRunning: boolean
  result: any
  error: string
}

function tryParseJson(text: any): any {
  if (typeof text !== 'string') {
    return undefined
  }
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

// Server error detail from a rejected request — in `err.errors` (worker) or responseJSON/responseText
// (jQuery), not `err.message`.
function errorDetail(err: any): string | undefined {
  const fromErrors = (arr: any): string | undefined =>
    Array.isArray(arr) && arr.length
      ? arr.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean).join('\n')
      : undefined
  const parsed = tryParseJson(err?.responseText)
  return (
    fromErrors(err?.errors) ||
    fromErrors(err?.responseJSON?.errors) ||
    fromErrors(parsed?.errors) ||
    (typeof err?.responseText === 'string' && err.responseText.trim() ? err.responseText : undefined)
  )
}

function formatRunError(err: any): string {
  const status = err?.statusCode ?? err?.status
  const header = err?.message || (status ? `HTTP error ${status}` : 'Test run failed.')
  const detail = errorDetail(err)
  return detail && detail !== header ? `${header}\n${detail}` : header
}

/** One produced entity, flattened out of its result row for the table view. */
interface ITestRunItem {
  key: string
  rowNum: number
  alias: string
  action: string
  valid: boolean
  validationErrors: any[]
  entity: any
  title: string
}

/** A row-level problem (a hard error, or an alias the config skipped) surfaced above the table. */
interface ITestRunIssue {
  rowNum: number
  label: string
  message: string
}

// displayName is a computed getter off the resolved schema; undefined when the schema isn't loaded.
function entityDisplayName(content: any): string | undefined {
  try {
    return content ? new Entity(content, apis).displayName : undefined
  } catch {
    return undefined
  }
}

/** Flattens result.rows[].entities[] into one table item per produced entity. */
function flattenItems(result: any): ITestRunItem[] {
  const items: ITestRunItem[] = []
  _.forEach(result?.rows || [], (row: any) => {
    _.forEach(row.entities || [], (ar: any, idx: number) => {
      items.push({
        key: `${row.rowNum}:${ar.alias}:${idx}`,
        rowNum: row.rowNum,
        alias: ar.alias,
        action: ar.action,
        valid: ar.simulation?.valid ?? true,
        validationErrors: ar.simulation?.validationErrors || [],
        entity: ar.entity,
        title: entityDisplayName(ar.entity) || ar.alias,
      })
    })
  })
  return items
}

/** Collects per-row errors and skipped aliases so they still show once the cards are gone. */
function collectIssues(result: any): ITestRunIssue[] {
  const issues: ITestRunIssue[] = []
  _.forEach(result?.rows || [], (row: any) => {
    _.forEach(row.errors || [], (err: any) =>
      issues.push({ rowNum: row.rowNum, label: 'error', message: err?.message ?? String(err) })
    )
    _.forEach(_.toPairs(row.skips || {}), ([alias, message]) =>
      issues.push({ rowNum: row.rowNum, label: `skipped ${alias}`, message: message as string })
    )
  })
  return issues
}

function ValidBadge({ valid }: { valid: boolean }) {
  return (
    <span
      className={valid ? 'c-testPayload-valid' : 'c-errorColor'}
      style={{ fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {valid ? '✓ valid' : '✗ invalid'}
    </span>
  )
}

// Portaled to body so the app's stacking contexts don't clip it.
const MAXIMIZED_OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2000,
  padding: 16,
}

const DETAIL_DEFAULT_WIDTH = 520
// SplitPane absolutely positions its panes, so the inline (non-maximized) wrapper needs a fixed height.
const INLINE_SPLIT_HEIGHT = 480

/**
 * Test-run results table (Row · Entity · Name · Action · Status). Clicking a row opens a table/detail
 * split view; "Maximize" pops it into a full-viewport overlay.
 */
class TestRunResults extends React.Component<
  { result: any },
  {
    selectedKey: string | null
    isMaximized: boolean
    containerWidth: number
    containerHeight: number
  }
> {
  // Table pane is measured by a ResizeObserver; the Table is sized to it.
  private resizeObserver: ResizeObserver | null = null
  private containerNode: HTMLDivElement | null = null
  private measureRaf = 0

  constructor(props: TestRunResults['props']) {
    super(props)
    this.state = {
      selectedKey: null,
      isMaximized: false,
      containerWidth: 0,
      containerHeight: 0,
    }
  }

  public componentWillUnmount() {
    this.resizeObserver?.disconnect()
    if (this.measureRaf) {
      window.cancelAnimationFrame(this.measureRaf)
    }
  }

  public componentDidUpdate(prevProps: TestRunResults['props']) {
    // A fresh run replaces the result object; drop any stale selection.
    if (prevProps.result !== this.props.result) {
      this.setState({ selectedKey: null })
    }
  }

  // Measure on the next frame, not inside the ResizeObserver callback, to avoid a resize feedback loop.
  private scheduleMeasure = () => {
    if (this.measureRaf) {
      return
    }
    this.measureRaf = window.requestAnimationFrame(() => {
      this.measureRaf = 0
      const node = this.containerNode
      if (!node) {
        return
      }
      const width = node.clientWidth
      const height = node.clientHeight
      const next: { containerWidth?: number; containerHeight?: number } = {}
      if (width && width !== this.state.containerWidth) {
        next.containerWidth = width
      }
      if (height && height !== this.state.containerHeight) {
        next.containerHeight = height
      }
      if (!_.isEmpty(next)) {
        this.setState(next as Pick<TestRunResults['state'], 'containerWidth' | 'containerHeight'>)
      }
    })
  }

  private setContainerRef = (node: HTMLDivElement | null) => {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.containerNode = node
    if (!node) {
      return
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure)
      this.resizeObserver.observe(node)
    }
    this.scheduleMeasure()
  }

  private getColumns(isSplit: boolean): Column[] {
    const columns: Column[] = [
      { path: 'rowNum', label: 'Row', width: 56 },
      {
        path: 'alias',
        label: 'Entity',
        width: 120,
        formatter: (alias: string) => <span className='c-testPayload-aliasChip'>{alias}</span>,
      },
      { path: 'title', label: 'Name', width: 200, flexGrow: 1, isPrimary: true },
      { path: 'action', label: 'Action', width: 90 },
      {
        path: 'valid',
        label: 'Status',
        width: 90,
        // Icon-only; the full wording is in the detail header.
        formatter: (valid: boolean) => (
          <span className={valid ? 'c-testPayload-valid' : 'c-errorColor'} style={{ fontWeight: 600 }}>
            {valid ? '✓' : '✗'}
          </span>
        ),
      },
    ]
    // The split-view table pane is narrow; drop Action to keep Name legible.
    return isSplit ? columns.filter((c) => c.path !== 'action') : columns
  }

  public render() {
    const { result } = this.props
    const items = flattenItems(result)
    const issues = collectIssues(result)
    const selectedItem = items.find((i) => i.key === this.state.selectedKey) || null
    const body = this.renderBody(items, issues, selectedItem)

    if (this.state.isMaximized) {
      return ReactDOM.createPortal(
        <div className='u-flexColumn c-testPayload-overlay' style={MAXIMIZED_OVERLAY_STYLE}>
          {body}
        </div>,
        document.body
      )
    }
    return <div className='u-bumperTop'>{body}</div>
  }

  private renderBody(
    items: ITestRunItem[],
    issues: ITestRunIssue[],
    selectedItem: ITestRunItem | null
  ) {
    const { result } = this.props
    return (
      <>
        <div className='u-flex u-alignItems--center u-bumperBottom--sm'>
          <span>
            {result.rowCount} row(s), {result.failureCount} failed.
          </span>
          <Button
            style={{ marginLeft: 'auto' }}
            rightIcon={this.state.isMaximized ? IconNames.MINIMIZE : IconNames.MAXIMIZE}
            onClick={() => this.setState((prev) => ({ isMaximized: !prev.isMaximized }))}
          />
        </div>
        {issues.length > 0 && this.renderIssues(issues)}
        {selectedItem ? this.renderSplit(items, selectedItem) : (
          <div ref={this.setContainerRef} style={{ minWidth: 0, overflow: 'hidden' }}>
            {this.renderTable(items, false)}
          </div>
        )}
      </>
    )
  }

  // Table + detail split (MasterDetail). `detailPanelMaxSize={-360}` reserves 360px min for the table.
  private renderSplit(items: ITestRunItem[], selectedItem: ITestRunItem) {
    const wrapperStyle: React.CSSProperties = this.state.isMaximized
      ? { display: 'flex', flex: '1 1 auto', minHeight: 0 }
      : { display: 'flex', height: INLINE_SPLIT_HEIGHT }
    return (
      <div style={wrapperStyle}>
        <MasterDetail
          showDetailPanel={true}
          detailPanelDefaultSize={DETAIL_DEFAULT_WIDTH}
          detailPanelMinSize={320}
          detailPanelMaxSize={-360}
          detailPanelSplit='vertical'
          masterPanel={this.renderTablePane(items)}
          detailPanel={this.renderDetailPanel(selectedItem)}
        />
      </div>
    )
  }

  // Keyed by the selected item so the card remounts on selection change.
  private renderDetailPanel(item: ITestRunItem) {
    return (
      <div
        key={item.key}
        className='u-flexColumn'
        style={{ height: '100%', flex: 1, minWidth: 0 }}
      >
        <div
          className='u-flex u-alignItems--center c-testPayload-detailHeader'
          style={{ padding: '6px 10px' }}
        >
          <span className='c-testPayload-aliasChip'>{item.alias}</span>
          <span className='u-bumperLeft--xs' style={{ fontWeight: 600 }}>
            {item.action}
          </span>
          <span className='u-bumperLeft--sm'>
            <ValidBadge valid={item.valid} />
          </span>
          <Button
            style={{ marginLeft: 'auto' }}
            rightIcon={IconNames.CROSS}
            onClick={() => this.setState({ selectedKey: null })}
          />
        </div>
        {item.validationErrors.length > 0 && (
          <ul className='c-errorColor' style={{ margin: 0, padding: '6px 10px 6px 28px' }}>
            {item.validationErrors.map((err, i) => (
              <li key={i}>
                {err.field ? `${err.field}: ` : ''}
                {err.message}
              </li>
            ))}
          </ul>
        )}
        <TransientEntityDetailCard content={item.entity} />
      </div>
    )
  }

  // Absolute so the table's width can't pin the pane and block the divider.
  private renderTablePane(items: ITestRunItem[]) {
    return (
      <div
        ref={this.setContainerRef}
        style={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          {this.renderTable(items, true)}
        </div>
      </div>
    )
  }

  private renderIssues(issues: ITestRunIssue[]) {
    return (
      <ul className='c-placeholderColor u-bumperBottom--sm'>
        {issues.map((issue, i) => (
          <li key={i}>
            <strong>Row {issue.rowNum}</strong> — {issue.label}: {issue.message}
          </li>
        ))}
      </ul>
    )
  }

  private renderTable(items: ITestRunItem[], isSplit: boolean) {
    if (items.length === 0) {
      return <div className='c-placeholderColor u-innerBumper'>No entities produced.</div>
    }
    const selectedItem = items.find((i) => i.key === this.state.selectedKey)
    // A cap, not a fixed height: fill the pane when split, fixed cap otherwise.
    const width = this.state.containerWidth
    const maxHeight = isSplit
      ? this.state.containerHeight || INLINE_SPLIT_HEIGHT
      : this.state.isMaximized ? 900 : 440
    return (
      <Table
        frames={undefined}
        columns={this.getColumns(isSplit)}
        rows={items}
        keyPath={(row: any) => row.key}
        isSelectable={false}
        enableMultiSelect={false}
        selection={selectedItem ? [selectedItem] : []}
        onRowClick={(_selection: any, row: any) => this.setState({ selectedKey: row.key })}
        width={width || undefined}
        maxHeight={maxHeight}
        rowHeight={37}
      />
    )
  }
}

/**
 * Sandbox for a Multi Entity Ingestion Payload Config: runs the draft (unsaved) config against
 * a pasted sample or uploaded CSV through the real ingestion loop without writing, and shows the
 * entities each row would produce with their action and validation. Rendered as the "Sandbox" tab;
 * gating (super-admin + payload-config mixin) is the caller's.
 */
export class TestPayloadConfig extends React.Component<
  ITestPayloadConfigProps,
  ITestPayloadConfigState
> {
  private dropzone?: Dropzone

  constructor(props: ITestPayloadConfigProps) {
    super(props)
    this.state = {
      sample: '',
      fileName: '',
      isRunning: false,
      result: null,
      error: '',
    }
  }

  public render() {
    return (
      <div className='u-innerBumper'>
        <div
          className='u-flex u-alignItems--center u-bumperBottom--sm'
          style={{
            gap: 8,
            padding: '10px 12px',
            borderRadius: 6,
            background: Colors.LIGHT_GRAY5,
            border: `1px solid ${Colors.LIGHT_GRAY2}`,
          }}
        >
          <Icon icon={IconNames.INFO_SIGN} color={Colors.GRAY2} />
          <span className='c-placeholderColor'>
            Test run — nothing is written. Shows the entities each input row would produce.
          </span>
        </div>

        {this.renderSourceInput()}

        <div className='u-flex u-alignItems--center u-bumperTop--sm' style={{ gap: 12 }}>
          <Button
            intent={Intent.PRIMARY}
            isDisabled={!this.canRun()}
            onClick={this.handleRun}
          >
            {this.state.isRunning ? 'Running…' : 'Run'}
          </Button>
          {this.state.sample.length > 0 && !this.state.isRunning && (
            <a className='c-placeholderColor' onClick={this.handleClear}>
              Clear
            </a>
          )}
        </div>

        {this.renderResult()}
      </div>
    )
  }

  // One control for paste, drop, or browse; disableClick keeps textarea clicks for cursor/selection.
  private renderSourceInput() {
    const { sample, fileName } = this.state
    return (
      <div>
        <div className='u-flex u-alignItems--baseline u-bumperBottom--xs' style={{ gap: 8 }}>
          <span style={{ fontWeight: 600 }}>Sample payload</span>
          <span className='c-placeholderColor' style={{ fontSize: 12 }}>
            CSV — your raw source columns (the ones the munging expressions reference), one row per record
          </span>
        </div>

        <Dropzone
          ref={(ref) => (this.dropzone = ref ?? undefined)}
          accept='.csv,text/csv,text/plain'
          multiple={false}
          disableClick={true}
          disablePreview={true}
          onDrop={this.handleDrop}
          className='u-block'
          activeClassName='is-active'
        >
          {({ isDragActive }) => (
            <div
              style={{
                position: 'relative',
                borderRadius: 6,
                border: `2px dashed ${isDragActive ? Colors.BLUE3 : Colors.LIGHT_GRAY1}`,
                background: isDragActive ? Colors.BLUE5 : Colors.WHITE,
                transition: 'border-color 120ms, background 120ms',
                padding: 4,
              }}
            >
              <div
                className='u-flex u-alignItems--center'
                style={{
                  gap: 6,
                  padding: '6px 8px',
                  fontSize: 12,
                  color: Colors.GRAY1,
                  borderBottom: `1px solid ${Colors.LIGHT_GRAY3}`,
                }}
              >
                <Icon icon={IconNames.CLOUD_UPLOAD} color={Colors.GRAY2} iconSize={14} />
                <span>
                  Drag a CSV file here, paste below, or{' '}
                  <a onClick={() => this.dropzone?.open()}>browse</a>
                </span>
              </div>
              <textarea
                className='c-input'
                spellCheck={false}
                style={{
                  width: '100%',
                  minHeight: 150,
                  border: 'none',
                  boxShadow: 'none',
                  resize: 'vertical',
                  padding: '8px',
                  background: 'transparent',
                  fontFamily: 'Menlo, Monaco, Consolas, monospace',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
                value={sample}
                placeholder={'Appt Number,Trailer Number\nA123,TRL-9'}
                onChange={(e) => this.setState({ sample: e.target.value, fileName: '' })}
              />
              {isDragActive && (
                <div
                  className='u-flex u-alignItems--center u-justifyContent--center c-testPayload-dropOverlay'
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 6,
                    color: Colors.BLUE2,
                    fontWeight: 600,
                    pointerEvents: 'none',
                  }}
                >
                  <Icon icon={IconNames.IMPORT} color={Colors.BLUE2} />
                  <span style={{ marginLeft: 8 }}>Drop to load the file</span>
                </div>
              )}
            </div>
          )}
        </Dropzone>

        {fileName ? (
          <div
            className='u-flex u-alignItems--center u-bumperTop--xs'
            style={{ gap: 6, fontSize: 12, color: Colors.GREEN2 }}
          >
            <Icon icon={IconNames.TICK_CIRCLE} color={Colors.GREEN3} iconSize={14} />
            <span>
              Loaded <strong>{fileName}</strong> — {sample.length.toLocaleString()} chars
            </span>
          </div>
        ) : (
          <div className='c-placeholderColor u-bumperTop--xs' style={{ fontSize: 12 }}>
            {sample ? `${sample.length.toLocaleString()} chars` : ' '}
          </div>
        )}
      </div>
    )
  }

  private handleClear = () => {
    this.setState({ sample: '', fileName: '', result: null, error: '' })
  }

  private handleDrop = (accepted: File[]) => {
    const file = accepted && accepted[0]
    if (!file) {
      return
    }
    const reader = new FileReader()
    reader.onload = () => this.setState({ sample: String(reader.result || ''), fileName: file.name })
    reader.readAsText(file)
  }

  private canRun(): boolean {
    const { sample, isRunning } = this.state
    if (isRunning) {
      return false
    }
    return _.trim(sample).length > 0
  }

  private handleRun = async () => {
    const { entity } = this.props
    const { sample } = this.state
    const payloadConfig = entity.get(PAYLOAD_CONFIG_PATH) ?? _.get(entity.content, PAYLOAD_CONFIG_PATH)

    this.setState({ isRunning: true, result: null, error: '' })

    try {
      const result = await apis.sandboxIngestionPayloadConfig({
        payloadConfig,
        sample,
        runPreProcessor: true,
      })
      const parsed = _.isString(result) ? JSON.parse(result) : result
      this.setState({ isRunning: false, result: parsed })
    } catch (err) {
      this.setState({ isRunning: false, error: formatRunError(err) })
    }
  }

  private renderResult() {
    const { result, error } = this.state
    if (error) {
      return <pre className='u-bumperTop c-errorColor'>{error}</pre>
    }
    if (!result) {
      return null
    }
    return <TestRunResults result={result} />
  }
}
